import {
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    Grid,
    IconButton,
    InputLabel,
    makeStyles,
    MenuItem,
    Popover,
    Select,
    TextField,
    Tooltip,
    Typography,
} from "@material-ui/core"
import {
    AddAPhoto,
    DeleteForever,
    DeleteOutline,
    DragIndicator,
    Edit,
    InfoOutlined,
} from "@material-ui/icons"
import { memo, useCallback, useRef, useState } from "react"
import { redimensionarImagemProduto } from "../../../functions/imagens"
import { inativarOpcao, vincularImagemProduto } from "../../../services/cardapio"
import useAppStore from "../../../stores/AppStore"
import useLoadingStore from "../../../stores/LoadingStore"
import { useSnackbar } from "../../../utils/notificacoes"
import { useCardapioContext } from "./CardapioContext"
import DialogConfirmacao from "./DialogConfirmacao"
import DialogEscolherOrigemFoto from "./DialogEscolherOrigemFoto"
import DialogGaleriaFotos from "./DialogGaleriaFotos"
import DisponibilidadeSwitch from "./DisponibilidadeSwitch"
import { formatarPreco, TextoEditavel } from "./TextoEditavel"

const useStyles = makeStyles(theme => ({
    // Linha do item: layout em colunas (flex) para alinhar preço/status/ações
    // entre todas as linhas. Sem card/borda própria — a separação vem da
    // divisória sutil do ListItem; no hover, um cinza extremamente claro.
    container: {
        display: "flex",
        alignItems: "center",
        columnGap: 10,
        rowGap: 4,
        flexWrap: "wrap",
        width: "100%",
        padding: "4px 6px",
        borderRadius: 8,
        backgroundColor: "#fff",
        transition: "background-color 0.15s ease",
        "&:hover": { backgroundColor: "#f6f8f7" },
        "& .MuiOutlinedInput-root": {
            borderRadius: 10,
            "& fieldset": { borderColor: "#e0e0e0" },
            "&:hover fieldset": { borderColor: "#4caf50" },
            "&.Mui-focused fieldset": { borderColor: "#4caf50" },
        },
        [theme.breakpoints.up("md")]: {
            flexWrap: "nowrap",
        },
    },
    // Destaque enquanto a opção está sendo arrastada (reordenação).
    containerArrastando: {
        backgroundColor: "#e8f5e9 !important",
        boxShadow: "inset 0 0 0 2px rgba(var(--cor-botao-salvar-rgb),0.5)",
        borderRadius: 8,
    },
    // Coluna: alça de arrastar.
    colArrastar: {
        flex: "0 0 auto",
    },
    // Coluna: número da ordem de exibição (click-to-edit, igual aos demais campos).
    colOrdem: {
        flex: "0 0 32px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        minWidth: 55,
    },
    ordemValor: {
        fontWeight: 700,
        color: "#6b7883",
        minWidth: 20,
        textAlign: "center",
    },
    // Coluna: foto (menor e quadrada).
    containerImg: {
        flex: "0 0 auto",
        position: "relative",
        width: "52px",
        height: "52px",
        backgroundColor: "#eee",
        border: "2px dashed #ccc",
        borderRadius: 8,
        overflow: "hidden",
        cursor: "pointer",
    },
    addAPhoto: {
        margin: "auto",
        display: "block",
        width: "34%",
        height: "34%",
        color: "rgba(0,0,0,0.5)",
    },
    input: {
        display: "none",
    },
    // Coluna: nome (destaque) + descrição (menor, cinza) empilhados.
    colNome: {
        flex: "1 1 auto",
        minWidth: 150,
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
    },
    // Coluna: preço — largura fixa e alinhado à direita para casar entre as linhas.
    colPreco: {
        flex: "0 0 92px",
        textAlign: "right",
    },
    // Coluna: status (toggle + rótulo Ativo/Inativo).
    colStatus: {
        flex: "0 0 auto",
        minWidth: 104,
        display: "flex",
        alignItems: "center",
        gap: 4,
    },
    statusLabel: {
        fontSize: "0.75rem",
        fontWeight: 600,
        whiteSpace: "nowrap",
    },
    statusAtivo: { color: "#2e7d32" },
    statusInativo: { color: "var(--cor-erro)" },
    // Coluna: ações (editar + excluir agrupadas e próximas).
    colAcoes: {
        flex: "0 0 auto",
        display: "flex",
        alignItems: "center",
        gap: 2,
    },
    // Valores em modo leitura (click-to-edit), espelhando o estilo do produto.
    nomeOpcao: {
        fontWeight: 600,
        color: "#2c3e50",
        wordBreak: "break-word",
    },
    descricaoOpcao: {
        fontSize: "0.75rem",
        color: "rgba(0,0,0,0.55)",
    },
    precoOpcao: {
        width: "100%",
        textAlign: "right",
        fontWeight: 600,
        color: "#2e7d32",
        whiteSpace: "nowrap",
    },
    noSpinner: {
        "& input[type=number]": {
            MozAppearance: "textfield",
        },
        "& input[type=number]::-webkit-outer-spin-button": {
            WebkitAppearance: "none",
            margin: 0,
        },
        "& input[type=number]::-webkit-inner-spin-button": {
            WebkitAppearance: "none",
            margin: 0,
        },
    },
    // Estilo alinhado ao dialog de edição de produto (DialogProdutos): título
    // sóbrio, inputs arredondados com foco verde e botão de conclusão em pílula.
    dialogTitulo: {
        backgroundColor: "white",
        "& .MuiTypography-root": {
            fontWeight: 600,
            color: "#2c3e50",
        },
    },
    dialogContainer: {
        backgroundColor: "white",
        "& .MuiOutlinedInput-root": {
            borderRadius: 12,
            "& fieldset": { borderColor: "#e0e0e0" },
            "&:hover fieldset": { borderColor: "#4caf50" },
            "&.Mui-focused fieldset": { borderColor: "#4caf50" },
        },
    },
    dialogBotoes: {
        backgroundColor: "white",
    },
    botaoConcluir: {
        borderRadius: 25,
        textTransform: "none",
        fontWeight: 600,
        padding: "8px 22px",
        transition: "all 0.2s ease",
        backgroundColor: "var(--cor-botao-salvar)",
        color: "white",
        boxShadow: "0 4px 15px rgba(var(--cor-botao-salvar-rgb),0.3)",
        "&:hover": {
            backgroundColor: "var(--cor-botao-salvar-hover)",
            transform: "translateY(-2px)",
            boxShadow: "0 6px 20px rgba(var(--cor-botao-salvar-rgb),0.4)",
        },
    },
}))

const ItemAdicional = memo(function ItemAdicional({
    produto,
    passo,
    item,
    termoBusca,
    dragHandleProps,
    arrastando,
    onDefinirOrdem,
}) {
    const {
        handleExcluirOpcao,
        handleIdsEditar,
        handleCadastrarImagem,
        setCategorias,
        estabelecimento,
    } = useCardapioContext()

    const setLoading = useLoadingStore(s => s.setLoading)
    const { enqueueSnackbar } = useSnackbar()
    const autorizacao = localStorage.getItem("chaveSistema")
    const configuracoes = useAppStore(s => s.configuracoes)
    const parametros = configuracoes.user.parametros

    const alertStart = (msg, tipo) => {
        enqueueSnackbar(msg, { variant: tipo })
    }

    const fileInputRef = useRef(null)
    const handleDivClick = useCallback(() => {
        fileInputRef.current.click()
    }, [])

    const handleFileChange = useCallback(
        async event => {
            const file = event.target.files[0]

            try {
                const arquivoRedimensionado = await redimensionarImagemProduto(file)
                const nomeArquivo = file.name
                const idProduto = produto.id
                const idOpcao = item.id
                const idPasso = passo.id

                if (file) {
                    handleIdsEditar(produto.id)
                    handleCadastrarImagem(
                        nomeArquivo,
                        idProduto,
                        idPasso,
                        idOpcao,
                        arquivoRedimensionado
                    )
                }
            } catch (error) {
                alertStart(`${error}`, "error")
            }
        },
        [produto.id, item.id, passo.id, handleIdsEditar, handleCadastrarImagem, alertStart]
    )

    const [escolhaAberta, setEscolhaAberta] = useState(false)
    const [galeriaAberta, setGaleriaAberta] = useState(false)

    const handleSelecionarGaleria = async url => {
        setGaleriaAberta(false)
        setLoading(true)
        try {
            const resp = await vincularImagemProduto(
                estabelecimento?.id,
                null,
                item.id,
                url,
                autorizacao
            )
            if (resp?.retornoErro) {
                alertStart(resp.mensagem || "Não foi possível vincular a imagem.", "error")
            } else {
                item.urlImagem = url
                setCategorias(prev => [...prev])
                alertStart("Imagem atualizada com sucesso", "success")
            }
        } catch (error) {
            console.error(error)
        } finally {
            setLoading(false)
        }
    }

    const classes = useStyles()

    // const handleDragStart = index => {
    //     setDraggingAdicionais(index)
    // }
    // const handleDragEnd = () => {
    //     setDraggingAdicionais(null)
    // }

    const [disponivel, setDisponivel] = useState(item?.ativo)
    const handleChangeDisponibilidadeOpcao = useCallback(async () => {
        setLoading(true)
        const response = await inativarOpcao(item.id, disponivel, autorizacao)
        if (response.retornoErro) {
            console.error(response)
            alertStart(response.mensagem, "error")
        } else {
            setDisponivel(prev => !prev)
            console.warn("inativarOpcao => ", response)
            alertStart("Disponibilidade da opção alterada com sucesso", "success")
        }
        setLoading(false)
    }, [item.id, disponivel, autorizacao, setLoading, alertStart])

    const [tipoProducao, setTipoProducao] = useState(item?.tipoProducao)
    const handleTipoProducao = useCallback(
        e => {
            // "AUTO" é apenas um sentinela de UI (Select não pode ter valor vazio,
            // senão a label "CFOP" não sobe e fica em cima do texto). Persiste null.
            const valor = e.target.value === "AUTO" ? null : e.target.value
            item.tipoProducao = valor
            setTipoProducao(valor)
        },
        [item]
    )
    const [anchorRecomendacaoCfop, setAnchorRecomendacaoCfop] = useState(null)

    const [removiImagem, setRemoviImagem] = useState(false)
    const [openEditar, setOpenEditar] = useState(false)
    const [confirmarExcluir, setConfirmarExcluir] = useState(false)

    // Edição inline click-to-edit (igual ao produto): 0=nome, 1=preço, 2=descrição.
    // Tab/Shift+Tab move a edição para o próximo/anterior campo.
    const [campoAtivo, setCampoAtivo] = useState(null)
    const navegarCampo = useCallback((de, dir) => {
        const prox = de + dir
        setCampoAtivo(prox >= 0 && prox <= 2 ? prox : null)
    }, [])

    const [ordemAtiva, setOrdemAtiva] = useState(false)
    const [ordemVersao, setOrdemVersao] = useState(0)

    return (
        <>
            <Dialog open={openEditar} onClose={() => setOpenEditar(false)} maxWidth="sm" fullWidth>
                <DialogTitle className={classes.dialogTitulo}>Editar opção</DialogTitle>
                <DialogContent className={classes.dialogContainer}>
                    <Grid container spacing={2} style={{ marginTop: 4 }}>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                type="number"
                                variant="outlined"
                                label="Código PDV"
                                size="small"
                                InputLabelProps={{ shrink: true }}
                                defaultValue={item.codigo}
                                onChange={e => {
                                    handleIdsEditar(produto.id)
                                    item.codigo = e.target?.value
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl
                                size="small"
                                fullWidth
                                variant="outlined"
                                disabled={!parametros.impressaoAlternada}
                            >
                                <InputLabel>Impressora</InputLabel>
                                <Select
                                    label="Impressora"
                                    defaultValue={
                                        parametros.impressaoAlternada ? item.impressaoAlternada : 0
                                    }
                                    onChange={e => {
                                        handleIdsEditar(produto.id)
                                        item.impressaoAlternada = e.target.value
                                    }}
                                >
                                    <MenuItem value={0}> - </MenuItem>
                                    <MenuItem value={1}> 1ª impressora</MenuItem>
                                    <MenuItem value={2}> 2ª impressora</MenuItem>
                                    <MenuItem value={3}> 3ª impressora</MenuItem>
                                    <MenuItem value={4}> 4ª impressora</MenuItem>
                                    <MenuItem value={5}> 5ª impressora</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} container alignItems="center" style={{ gap: "0.25em" }}>
                            <FormControl
                                size="small"
                                variant="outlined"
                                style={{ minWidth: "200px", flex: 1 }}
                            >
                                <InputLabel>CFOP</InputLabel>
                                <Select
                                    label="CFOP"
                                    value={tipoProducao ?? "AUTO"}
                                    onChange={e => {
                                        handleIdsEditar(produto.id)
                                        handleTipoProducao(e)
                                    }}
                                >
                                    <MenuItem value="AUTO">Automático</MenuItem>
                                    <MenuItem value="5101">Produção própria (5101)</MenuItem>
                                    <MenuItem value="5102">Revenda sem ST (5102)</MenuItem>
                                    <MenuItem value="5405">Revenda com ST (5405)</MenuItem>
                                </Select>
                            </FormControl>
                            <IconButton
                                size="small"
                                aria-label="Recomendações de CFOP"
                                onClick={e => setAnchorRecomendacaoCfop(e.currentTarget)}
                            >
                                <InfoOutlined fontSize="small" />
                            </IconButton>
                            <Popover
                                open={Boolean(anchorRecomendacaoCfop)}
                                anchorEl={anchorRecomendacaoCfop}
                                onClose={() => setAnchorRecomendacaoCfop(null)}
                                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                                transformOrigin={{ vertical: "top", horizontal: "left" }}
                            >
                                <div style={{ padding: "1em", maxWidth: "360px" }}>
                                    <Typography
                                        variant="subtitle2"
                                        style={{ marginBottom: "0.5em" }}
                                    >
                                        Recomendações de CFOP
                                    </Typography>
                                    <Typography variant="body2" style={{ marginBottom: "0.5em" }}>
                                        <strong>Automático:</strong> deixa o sistema decidir pelo
                                        nome do item (bebida vira revenda; os demais, produção
                                        própria).
                                    </Typography>
                                    <Typography variant="body2" style={{ marginBottom: "0.5em" }}>
                                        <strong>Produção própria (5101):</strong> produtos
                                        preparados ou fabricados pela própria loja.
                                    </Typography>
                                    <Typography variant="body2" style={{ marginBottom: "0.5em" }}>
                                        <strong>Revenda sem ST (5102):</strong> produtos comprados
                                        prontos e revendidos, sem substituição tributária.
                                    </Typography>
                                    <Typography variant="body2">
                                        <strong>Revenda com ST (5405):</strong> produtos revendidos
                                        com substituição tributária (ex.: bebidas).
                                    </Typography>
                                </div>
                            </Popover>
                        </Grid>
                        <Grid item xs={12} container alignItems="center">
                            <Checkbox
                                defaultChecked={item.informativo}
                                onChange={e => {
                                    handleIdsEditar(produto.id)
                                    item.informativo = e.target.checked
                                }}
                            />
                            <Typography>Apenas Informativo</Typography>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions className={classes.dialogBotoes}>
                    <Button className={classes.botaoConcluir} onClick={() => setOpenEditar(false)}>
                        Concluir
                    </Button>
                </DialogActions>
            </Dialog>

            <div
                className={`${classes.container} ${arrastando ? classes.containerArrastando : ""}`}
            >
                {/* Coluna: alça de arrastar */}
                <div className={classes.colArrastar}>
                    <IconButton
                        {...dragHandleProps}
                        size="small"
                        title="Arraste para reordenar"
                        aria-label="Arraste para reordenar"
                        style={{ cursor: "grab" }}
                    >
                        <DragIndicator fontSize="small" />
                    </IconButton>
                </div>

                <div className={classes.colOrdem}>
                    <TextoEditavel
                        key={`${item.ordemExib}-${ordemVersao}`}
                        valorInicial={item.ordemExib}
                        placeholder="0"
                        viewClassName={classes.ordemValor}
                        inputType="number"
                        inputClassName={classes.noSpinner}
                        inputProps={{ style: { textAlign: "center" } }}
                        ativo={ordemAtiva}
                        onAtivar={() => setOrdemAtiva(true)}
                        onFechar={() => {
                            setOrdemAtiva(false)
                            setOrdemVersao(v => v + 1)
                        }}
                        onCommit={valor => {
                            const num = parseInt(valor, 10)
                            if (!isNaN(num)) {
                                onDefinirOrdem && onDefinirOrdem(num)
                            }
                        }}
                    />
                </div>

                {/* Coluna: foto */}
                <div className={classes.containerImg} onClick={() => setEscolhaAberta(true)}>
                    {item.urlImagem && !removiImagem ? (
                        <img
                            style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                                objectPosition: "center",
                            }}
                            loading="lazy"
                            src={item.urlImagem}
                            alt=""
                        />
                    ) : (
                        <AddAPhoto className={classes.addAPhoto} />
                    )}
                    <input
                        type="file"
                        ref={fileInputRef}
                        onChange={handleFileChange}
                        className={classes.input}
                        accept="image/*"
                    />
                    {item.urlImagem && (
                        <div style={{ position: "absolute", top: 0, right: 0 }}>
                            <IconButton
                                size="small"
                                onClick={e => {
                                    e.stopPropagation()
                                    handleIdsEditar(produto.id)
                                    item.urlImagem = ""
                                    setRemoviImagem(true)
                                }}
                            >
                                <DeleteForever style={{ color: "var(--cor-erro)" }} fontSize="small" />
                            </IconButton>
                        </div>
                    )}
                </div>

                <DialogEscolherOrigemFoto
                    open={escolhaAberta}
                    onClose={() => setEscolhaAberta(false)}
                    onDispositivo={() => {
                        setEscolhaAberta(false)
                        handleDivClick()
                    }}
                    onGaleria={() => {
                        setEscolhaAberta(false)
                        setGaleriaAberta(true)
                    }}
                />
                <DialogGaleriaFotos
                    open={galeriaAberta}
                    onClose={() => setGaleriaAberta(false)}
                    onSelect={handleSelecionarGaleria}
                    idEstabelecimento={estabelecimento?.id}
                />

                {/* Coluna: nome + descrição */}
                <div className={classes.colNome}>
                    <TextoEditavel
                        valorInicial={item.nome.replace("Nova opção", "").trim()}
                        placeholder="Sem nome"
                        viewClassName={classes.nomeOpcao}
                        termoDestaque={(termoBusca ?? "").toLowerCase()}
                        ativo={campoAtivo === 0}
                        onAtivar={() => setCampoAtivo(0)}
                        onFechar={() => setCampoAtivo(c => (c === 0 ? null : c))}
                        onNavegar={dir => navegarCampo(0, dir)}
                        onCommit={valor => {
                            handleIdsEditar(produto.id)
                            item.nome = valor.replace("Nova opção", "").trim()
                        }}
                    />
                    <TextoEditavel
                        multiline
                        valorInicial={item.descricao}
                        placeholder="Adicionar descrição"
                        viewClassName={classes.descricaoOpcao}
                        ativo={campoAtivo === 2}
                        onAtivar={() => setCampoAtivo(2)}
                        onFechar={() => setCampoAtivo(c => (c === 2 ? null : c))}
                        onNavegar={dir => navegarCampo(2, dir)}
                        onCommit={valor => {
                            handleIdsEditar(produto.id)
                            item.descricao = valor
                        }}
                    />
                </div>

                {/* Coluna: preço */}
                <div className={classes.colPreco}>
                    <TextoEditavel
                        valorInicial={item.valor}
                        placeholder="R$ 0,00"
                        viewClassName={classes.precoOpcao}
                        formatarView={formatarPreco}
                        inputType="number"
                        inputClassName={classes.noSpinner}
                        inputProps={{ step: "0.01", inputMode: "decimal" }}
                        ativo={campoAtivo === 1}
                        onAtivar={() => setCampoAtivo(1)}
                        onFechar={() => setCampoAtivo(c => (c === 1 ? null : c))}
                        onNavegar={dir => navegarCampo(1, dir)}
                        onCommit={valor => {
                            handleIdsEditar(produto.id)
                            const num = parseFloat(String(valor).replace(",", "."))
                            item.valor = isNaN(num) ? 0 : num
                        }}
                    />
                </div>

                {/* Coluna: status (toggle + rótulo) */}
                <div className={classes.colStatus}>
                    <DisponibilidadeSwitch
                        disponivel={disponivel}
                        handleChangeDisponibilidadeOpcao={handleChangeDisponibilidadeOpcao}
                    />
                    <Typography
                        component="span"
                        className={`${classes.statusLabel} ${
                            disponivel ? classes.statusAtivo : classes.statusInativo
                        }`}
                    >
                        {disponivel ? "Ativo" : "Inativo"}
                    </Typography>
                </div>

                {/* Coluna: ações (editar + excluir) */}
                <div className={classes.colAcoes}>
                    <Tooltip title="Editar opção">
                        <IconButton
                            size="small"
                            aria-label="Editar opção"
                            onClick={() => setOpenEditar(true)}
                        >
                            <Edit fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Excluir opção">
                        <IconButton
                            size="small"
                            aria-label="Excluir opção"
                            onClick={() => setConfirmarExcluir(true)}
                        >
                            <DeleteOutline style={{ color: "var(--cor-erro)" }} fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </div>
            </div>

            <DialogConfirmacao
                open={confirmarExcluir}
                titulo="Excluir opção"
                mensagem="Tem certeza de que deseja excluir a opção"
                nomeItem={item.nome?.replace("Nova opção", "").trim() || "sem nome"}
                onCancelar={() => setConfirmarExcluir(false)}
                onConfirmar={() => {
                    setConfirmarExcluir(false)
                    handleExcluirOpcao(produto.id, passo.id, item.id)
                }}
            />
        </>
    )
})

export default ItemAdicional
